import { useAuth, useKaiProApiClient, useOrgMarket, useUserMutators } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { MarketPanel } from '../Markets/MarketPanel';

/** Reuses the established market editor from the project overview without leaving the project workspace. */
export const ProjectOverviewMarketFlyout = ({ closeFlyout, flyoutOpen, marketId }: {
  closeFlyout: () => void;
  flyoutOpen: boolean;
  marketId?: string | null;
}) => {
  const { userCan } = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const { marketsMutators, orgMarket: marketToEdit } = useOrgMarket(marketId);
  const { refetchAllUsers } = useUserMutators();

  /** Persists the market's attributes and the contractor membership selected in its editor. */
  const updateMarket = async (
    marketId: string,
    name: string,
    zipCodes: string[],
    orgContractorIds: string[],
    defaultTemplateId?: string | null,
    defaultManagerIds?: string[],
  ) => {
    const existingContractorIds = marketToEdit?.marketContractors?.map(contractor => contractor.orgContractor.id) ?? [];
    await Promise.all([
      ...orgContractorIds
        .filter(orgContractorId => !existingContractorIds.includes(orgContractorId))
        .map(orgContractorId => marketsMutators.addContractor(marketId, orgContractorId)),
      ...existingContractorIds
        .filter(orgContractorId => !orgContractorIds.includes(orgContractorId))
        .map(orgContractorId => marketsMutators.removeContractor(marketId, orgContractorId)),
    ]);
    await marketsMutators.update(marketId, {
      name,
      zipCodes,
      ...(defaultTemplateId != null ? { defaultTemplateId: defaultTemplateId || null } : {}),
      ...(defaultManagerIds != null ? { defaultManagerIds } : {}),
    });
    trackEvent(ANALYTICS_EVENTS.marketUpdated, {
      marketId,
      name,
      zipCodeCount: zipCodes.length,
      contractorCount: orgContractorIds.length,
    });
  };

  /** Synchronizes user membership after the market's details have been saved. */
  const updateUsersForMarket = async (marketId: string, userIds: string[]) => {
    const existingUserIds = (await kaiProApiClient.getUsersByOrgMarketId(marketId)).map(user => user.id);
    await Promise.all([
      ...userIds
        .filter(userId => !existingUserIds.includes(userId))
        .map(userId => kaiProApiClient.addUserToOrgMarket(marketId, userId)),
      ...existingUserIds
        .filter(userId => !userIds.includes(userId))
        .map(userId => kaiProApiClient.removeUserFromOrgMarket(marketId, userId)),
    ]);
    await refetchAllUsers();
  };

  if (!marketToEdit) return null;

  return (
    <MarketPanel
      closeFlyout={closeFlyout}
      flyoutOpen={flyoutOpen}
      marketToEdit={marketToEdit}
      readOnly={!userCan('update', 'OrgMarket')}
      updateMarket={updateMarket}
      updateUsersForMarket={updateUsersForMarket}
    />
  );
};
